import { useState } from 'react';
import AdminCharts from '../AdminCharts';

const stats = [
  { label: 'Aktívni klienti',    value: '12',       delta: '+2 tento mesiac',           icon: 'ri-user-3-line',            color: 'text-orange-400',  bg: 'bg-orange-500/10'  },
  { label: 'Mesačný obrat',      value: '9 840 €',  delta: '+18 % vs minulý m.',        icon: 'ri-money-euro-circle-line', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  { label: 'Prebieha projektov', value: '8',         delta: '2 odovzdanie tento týždeň', icon: 'ri-folder-3-line',          color: 'text-sky-400',     bg: 'bg-sky-500/10'     },
  { label: 'Čakajúce platby',    value: '3 150 €',  delta: '3 faktúry',                 icon: 'ri-bill-line',              color: 'text-amber-400',   bg: 'bg-amber-500/10'   },
];

const topClients = [
  { initials: 'BS', name: 'Bratislava Pet Shop', mrr: 1200, color: 'bg-emerald-600', plan: 'Growth',  status: 'active' },
  { initials: 'TF', name: 'TechFlow s.r.o.',     mrr: 2400, color: 'bg-sky-600',     plan: 'Scale',   status: 'active' },
  { initials: 'VB', name: 'VitaBenefit s.r.o.',  mrr: 980,  color: 'bg-violet-600',  plan: 'Starter', status: 'active' },
  { initials: 'PK', name: 'Petra Kovárová',       mrr: 650,  color: 'bg-pink-600',    plan: 'Starter', status: 'active' },
  { initials: 'ML', name: 'MotoLux SK',           mrr: 1600, color: 'bg-orange-600',  plan: 'Growth',  status: 'active' },
];

const openTasks = [
  { id: 'T-041', title: 'Nahrať reklamné sady',    client: 'TechFlow',    due: '31.3.2025', priority: 'high'   },
  { id: 'T-042', title: 'SEO audit Q1',            client: 'Pet Shop',    due: '2.4.2025',  priority: 'medium' },
  { id: 'T-043', title: 'Schváliť dizajn landing', client: 'VitaBenefit', due: '5.4.2025',  priority: 'medium' },
  { id: 'T-044', title: 'Texty pre Google Ads',    client: 'MotoLux',     due: '7.4.2025',  priority: 'low'    },
];

const priorityStyle: Record<string, string> = {
  high:   'bg-red-500/10 text-red-400 border-red-500/20',
  medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  low:    'bg-zinc-700/40 text-zinc-400 border-zinc-600/30',
};

interface Alert {
  id: string;
  type: 'overdue_invoice' | 'late_project' | 'expiring_soon';
  title: string;
  detail: string;
  meta: string;
  action: string;
  clientInitials: string;
  clientColor: string;
}

const initialAlerts: Alert[] = [
  {
    id: 'a1',
    type: 'overdue_invoice',
    title: 'Faktúra BOO-2025-018 po splatnosti',
    detail: 'AutoMech Prešov - 750 € · splatná 15.2.2025',
    meta: '42 dní po splatnosti',
    action: 'Zobraziť faktúru',
    clientInitials: 'MR',
    clientColor: 'bg-zinc-600',
  },
  {
    id: 'a2',
    type: 'late_project',
    title: 'Projekt „E-shop redesign" mešká',
    detail: 'VitaBenefit s.r.o. - fáza: Dizajn review',
    meta: 'Deadline 10.4.2025 · zostáva 12 dní',
    action: 'Otvoriť projekt',
    clientInitials: 'VB',
    clientColor: 'bg-violet-600',
  },
  {
    id: 'a3',
    type: 'expiring_soon',
    title: 'Faktúra BOO-2025-017 splatná o 2 dni',
    detail: 'JH Legal s.r.o. - 2 200 € · splatná 15.4.2025',
    meta: 'Urgentné – klient ešte neuhradil',
    action: 'Odoslať upomienku',
    clientInitials: 'JH',
    clientColor: 'bg-indigo-600',
  },
  {
    id: 'a4',
    type: 'late_project',
    title: 'Projekt „Facebook Ads kampaň" blíži sa deadline',
    detail: 'TechFlow s.r.o. - fáza: Spustenie',
    meta: 'Deadline 15.4.2025 · zostáva 17 dní',
    action: 'Otvoriť projekt',
    clientInitials: 'TF',
    clientColor: 'bg-sky-600',
  },
  {
    id: 'a5',
    type: 'overdue_invoice',
    title: 'Faktúra BOO-2025-020 čaká na úhradu',
    detail: 'Bratislava Pet Shop - 1 200 € · splatná 15.4.2025',
    meta: 'Splatná o 17 dní',
    action: 'Zobraziť faktúru',
    clientInitials: 'BS',
    clientColor: 'bg-emerald-600',
  },
];

const alertTypeMeta: Record<Alert['type'], { icon: string; border: string; badge: string; badgeText: string }> = {
  overdue_invoice: {
    icon:      'ri-alarm-warning-line',
    border:    'border-red-500/30',
    badge:     'bg-red-500/10 text-red-400',
    badgeText: 'Po splatnosti',
  },
  late_project: {
    icon:      'ri-time-line',
    border:    'border-amber-500/30',
    badge:     'bg-amber-500/10 text-amber-400',
    badgeText: 'Oneskorenierojektu',
  },
  expiring_soon: {
    icon:      'ri-error-warning-line',
    border:    'border-orange-500/30',
    badge:     'bg-orange-500/10 text-orange-400',
    badgeText: 'Blíži sa splatnosť',
  },
};

export default function AdminDashboard() {
  const [alerts, setAlerts] = useState<Alert[]>(initialAlerts);
  const [alertsOpen, setAlertsOpen] = useState(true);
  const [selectedYear, setSelectedYear] = useState(2025);

  const dismissAlert = (id: string) => setAlerts((prev) => prev.filter((a) => a.id !== id));
  const dismissAll   = () => setAlerts([]);

  const overdueCount  = alerts.filter((a) => a.type === 'overdue_invoice').length;
  const lateCount     = alerts.filter((a) => a.type === 'late_project').length;
  const expiringCount = alerts.filter((a) => a.type === 'expiring_soon').length;

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">

      {/* ── Stats ── */}
      <div className="grid grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-zinc-500 text-xs font-medium">{s.label}</p>
                <p className="text-white text-2xl font-black mt-1">{s.value}</p>
                <p className="text-zinc-600 text-[11px] mt-1">{s.delta}</p>
              </div>
              <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
                <i className={`${s.icon} ${s.color} text-lg`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Alerts ── */}
      {alerts.length > 0 && (
        <div className="bg-zinc-900 border border-red-500/20 rounded-2xl overflow-hidden">
          {/* Alert header */}
          <div className="flex items-center gap-3 px-5 py-3.5 border-b border-zinc-800">
            <div className="w-7 h-7 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
              <i className="ri-alarm-warning-line text-red-400 text-sm" />
            </div>
            <div className="flex items-center gap-2 flex-1">
              <p className="text-white text-sm font-semibold">Upozornenia</p>
              <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                {alerts.length}
              </span>
              {/* Sub-counts */}
              <div className="flex items-center gap-1.5 ml-2">
                {overdueCount > 0 && (
                  <span className="bg-red-500/10 text-red-400 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-red-500/20">
                    {overdueCount} faktúry po splatnosti
                  </span>
                )}
                {expiringCount > 0 && (
                  <span className="bg-orange-500/10 text-orange-400 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-orange-500/20">
                    {expiringCount} blíži sa splatnosť
                  </span>
                )}
                {lateCount > 0 && (
                  <span className="bg-amber-500/10 text-amber-400 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-amber-500/20">
                    {lateCount} projekty oneskoria
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={dismissAll}
                className="text-zinc-600 hover:text-zinc-400 text-xs font-medium cursor-pointer transition-colors whitespace-nowrap"
              >
                Zamietnuť všetko
              </button>
              <button
                onClick={() => setAlertsOpen((v) => !v)}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-zinc-800 transition-all cursor-pointer"
              >
                <i className={`ri-arrow-${alertsOpen ? 'up' : 'down'}-s-line text-zinc-500 text-base`} />
              </button>
            </div>
          </div>

          {/* Alert items */}
          {alertsOpen && (
            <div className="divide-y divide-zinc-800/60">
              {alerts.map((alert) => {
                const meta = alertTypeMeta[alert.type];
                return (
                  <div
                    key={alert.id}
                    className={`flex items-center gap-4 px-5 py-3.5 border-l-2 ${meta.border} hover:bg-zinc-800/30 transition-all group`}
                  >
                    {/* Client avatar */}
                    <div className={`w-8 h-8 rounded-xl ${alert.clientColor} flex items-center justify-center flex-shrink-0`}>
                      <span className="text-white text-[10px] font-bold">{alert.clientInitials}</span>
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-zinc-200 text-xs font-semibold">{alert.title}</p>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.badge}`}>
                          {meta.badgeText}
                        </span>
                      </div>
                      <p className="text-zinc-500 text-[11px] mt-0.5">{alert.detail}</p>
                    </div>

                    {/* Meta */}
                    <div className="text-right flex-shrink-0 hidden lg:block">
                      <div className="flex items-center gap-1 justify-end">
                        <i className={`${meta.icon} text-zinc-600 text-xs`} />
                        <p className="text-zinc-500 text-[11px]">{alert.meta}</p>
                      </div>
                    </div>

                    {/* Action */}
                    <button className="flex-shrink-0 text-orange-400 hover:text-orange-300 text-xs font-semibold bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap hidden group-hover:flex items-center gap-1.5">
                      <i className="ri-arrow-right-line text-xs" />
                      {alert.action}
                    </button>
                    <button className="flex-shrink-0 text-orange-400 text-xs font-semibold bg-orange-500/10 border border-orange-500/20 px-3 py-1.5 rounded-lg group-hover:hidden whitespace-nowrap">
                      {alert.action}
                    </button>

                    {/* Dismiss */}
                    <button
                      onClick={() => dismissAlert(alert.id)}
                      className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-zinc-700 transition-all cursor-pointer flex-shrink-0 opacity-0 group-hover:opacity-100"
                    >
                      <i className="ri-close-line text-zinc-500 text-sm" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Dismissed state */}
      {alerts.length === 0 && (
        <div className="bg-zinc-900 border border-emerald-500/20 rounded-2xl px-5 py-3.5 flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
            <i className="ri-checkbox-circle-line text-emerald-400 text-sm" />
          </div>
          <p className="text-emerald-400 text-sm font-medium">Žiadne aktívne upozornenia - všetko je v poriadku!</p>
          <button
            onClick={() => setAlerts(initialAlerts)}
            className="ml-auto text-zinc-600 hover:text-zinc-400 text-xs cursor-pointer transition-colors whitespace-nowrap"
          >
            Obnoviť
          </button>
        </div>
      )}

      {/* ── Charts with year switcher ── */}
      <AdminCharts year={selectedYear} onYearChange={setSelectedYear} />

      {/* ── Open tasks + quick actions ── */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-white font-semibold text-sm">Prioritné úlohy</p>
            <span className="text-zinc-600 text-xs">4 otvorené</span>
          </div>
          <div className="space-y-2">
            {openTasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 bg-zinc-800/40 rounded-xl hover:bg-zinc-800/70 cursor-pointer transition-all">
                <div className="w-6 h-6 rounded-lg bg-zinc-700/60 flex items-center justify-center flex-shrink-0">
                  <i className="ri-task-line text-zinc-500 text-xs" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-200 text-xs font-medium truncate">{t.title}</p>
                  <p className="text-zinc-600 text-[10px] mt-0.5">{t.client} · splatnosť {t.due}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${priorityStyle[t.priority]}`}>
                  {t.priority === 'high' ? 'Urgentné' : t.priority === 'medium' ? 'Stredná' : 'Nízka'}
                </span>
                <span className="text-zinc-700 text-[10px] font-mono">{t.id}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <p className="text-white font-semibold text-sm mb-4">Rýchle akcie</p>
          <div className="space-y-2">
            {[
              { icon: 'ri-user-add-line',        color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Pridať klienta'   },
              { icon: 'ri-folder-add-line',       color: 'text-sky-400',     bg: 'bg-sky-500/10',     label: 'Nový projekt'     },
              { icon: 'ri-file-add-line',         color: 'text-amber-400',   bg: 'bg-amber-500/10',   label: 'Vystaviť faktúru' },
              { icon: 'ri-message-2-line',        color: 'text-orange-400',  bg: 'bg-orange-500/10',  label: 'Napísať správu'   },
              { icon: 'ri-bar-chart-upload-line', color: 'text-violet-400',  bg: 'bg-violet-500/10',  label: 'Nahrať report'    },
            ].map((a) => (
              <button key={a.label} className="w-full flex items-center gap-3 p-3 bg-zinc-800/40 hover:bg-zinc-800/70 rounded-xl cursor-pointer transition-all text-left whitespace-nowrap">
                <div className={`w-7 h-7 rounded-lg ${a.bg} flex items-center justify-center flex-shrink-0`}>
                  <i className={`${a.icon} ${a.color} text-sm`} />
                </div>
                <span className="text-zinc-300 text-xs font-medium">{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
